/** Numeric steps used across primitive palettes (light & dark scales). */
export const PALETTE_STEPS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100] as const;
export type PaletteStep = (typeof PALETTE_STEPS)[number];

/** One ramp: 10 = lightest tint, 100 = darkest shade (light-theme column). */
export type PaletteRamp = Record<PaletteStep, string>;

export type PrimitiveFamily =
  | 'primary'
  | 'secondary'
  | 'neutral'
  | 'error'
  | 'warning'
  | 'success'
  | 'info';

export interface PrimitivePalettes {
  primary: { light: PaletteRamp; dark: PaletteRamp };
  secondary: { light: PaletteRamp; dark: PaletteRamp };
  neutral: { light: PaletteRamp; dark: PaletteRamp };
  error: { light: PaletteRamp; dark: PaletteRamp };
  warning: { light: PaletteRamp; dark: PaletteRamp };
  success: { light: PaletteRamp; dark: PaletteRamp };
  info: { light: PaletteRamp; dark: PaletteRamp };
}

/** Interactive / component state tokens (per theme). */
export interface StateTokens {
  hover: string;
  pressed: string;
  focus: string;
  focusBorder: string;
  disabled: string;
  selected: string;
  hoverOverlay: string;
  pressedOverlay: string;
  disabledText: string;
  disabledBg: string;
  focusRing: string;
}

export interface StatePalettes {
  light: StateTokens;
  dark: StateTokens;
}

/** Layout semantics: surfaces, typography, borders, icons. */
export interface SemanticTokens {
  bg: { primary: string; secondary: string; tertiary: string; inverse: string };
  text: { primary: string; secondary: string; disabled: string; inverse: string };
  border: { default: string; subtle: string; strong: string };
  icon: { primary: string };
}

export interface SemanticPalettes {
  light: SemanticTokens;
  dark: SemanticTokens;
}

/** Frosted / vibrancy layer (navbar, sheets, cards) — translucent “glass” material. */
export interface GlassTokens {
  /** Panel fill (semi-transparent). */
  fill: string;
  /** Modals / elevated glass. */
  fillElevated: string;
  /** Hairline edge on glass. */
  stroke: string;
  /** Top / specular edge. */
  highlight: string;
  /** Inner depth / vignette multiply. */
  shade: string;
  /** Backdrop blur radius, e.g. `20px`. */
  blur: string;
  /** `backdrop-filter` saturation, e.g. `180%`. */
  saturate: string;
}

export interface GlassPalettes {
  light: GlassTokens;
  dark: GlassTokens;
}

/**
 * Viewport tiers (mobile-first min-widths, except `watchMax` for wrist / compact max-width).
 * Emitted as `--bp-*` on `:root`; Tailwind `--breakpoint-*` in `styles/base/variables.css` aliases these.
 */
export interface BreakpointTokens {
  /** Max width for compact / wrist-class layouts (`@media (max-width: …)`). */
  watchMax: string;
  /** Large phone / small phablet. */
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  twoXl: string;
  threeXl: string;
  fourXl: string;
}

/** Shared UI chrome: radius, motion, control metrics (not light/dark-specific). */
export interface UiTokens {
  radius: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    full: string;
  };
  motion: {
    durationXs: string;
    durationSm: string;
    durationMd: string;
    durationLg: string;
    durationXl: string;
    easingStandard: string;
    easingEmphasized: string;
    easingDecelerate: string;
  };
  /** Form controls, native buttons, dense inputs */
  control: {
    paddingX: string;
    paddingY: string;
    minHeightTouch: string;
    borderWidth: string;
    /** Field labels — one step below control value size (`0.875rem`). */
    labelFontSize: string;
    labelLineHeight: string;
    labelFontWeight: string;
    fontSize: string;
  };
  breakpoints: BreakpointTokens;
}

export interface DesignTheme {
  primitives: PrimitivePalettes;
  state: StatePalettes;
  semantic: SemanticPalettes;
  glass: GlassPalettes;
  /** Layout / interaction rhythm — emitted as `--ui-*` and `--bp-*` on `:root`. */
  ui: UiTokens;
  opacityBases: OpacityBases;
}

/** Optional explicit bases for opacity ramps (defaults to palette mid steps). */
export interface OpacityBases {
  /** Maps to “500” in specs — here palette step 50 on the light ramp. */
  primaryStep: PaletteStep;
  secondaryStep: PaletteStep;
  neutralDarkStep: PaletteStep;
  neutralLightStep: PaletteStep;
}

export const DEFAULT_OPACITY_BASES: OpacityBases = {
  primaryStep: 50,
  secondaryStep: 50,
  neutralDarkStep: 100,
  neutralLightStep: 10,
};

/** Flat payload suitable for JSON APIs (`{ "primitives.primary.light.10": "#..." }`). */
export type FlatThemeRecord = Record<string, string>;
